import { InputAdornment, InputBase } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type {
  BaseSingleInputFieldProps,
  DateValidationError,
  FieldSection,
  UseDateFieldProps,
} from "@mui/x-date-pickers-pro";
import { useFormat } from "@sinch/core/hooks/format";
import { useSettings } from "@sinch/core/providers/AppSettings";
import { MdsCalendarToday } from "@sinch/icons";
import { toLuxon } from "@sinch/utils/dateTime/toLuxon";
import type { DateTime } from "luxon";
import { isDate } from "ramda-adjunct";
import React, { useState } from "react";
import { MdsIcon } from "@sinch/components";
import { ConditionSelectProps } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionInput";

export const ConditionValueInputDate = ({
  onChange,
  value,
  onOpenChange,
  onBlur,
  readOnly,
}: ConditionSelectProps<Date>) => {
  const { date } = useFormat();
  const { timeZone } = useSettings();
  const [open, setOpen] = useState(false);

  const handleOpen = (openState: boolean) => {
    setOpen(openState);
    onOpenChange(openState);
  };

  return (
    <DatePicker<DateTime>
      label={value !== null && isDate(value) ? date.medium(value) : null}
      onChange={(newValue) => onChange(newValue?.toJSDate() ?? null)}
      onClose={() => {
        setOpen(false);
        onOpenChange(false);
      }}
      open={open}
      readOnly={readOnly}
      slotProps={{
        field: { setOpen: handleOpen, onBlur: onBlur } as any,
        nextIconButton: { size: "small" },
        previousIconButton: { size: "small" },
      }}
      slots={{ field: ButtonField }}
      timezone={timeZone}
      value={isDate(value) ? toLuxon(value) : null}
    />
  );
};

interface ButtonFieldProps
  extends UseDateFieldProps<DateTime, false>,
    BaseSingleInputFieldProps<DateTime | null, DateTime, FieldSection, false, DateValidationError> {
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

function ButtonField(props: ButtonFieldProps) {
  const { setOpen, label, disabled, InputProps: { ref } = {} } = props;

  return (
    <InputBase
      ref={ref}
      disabled={disabled}
      endAdornment={
        <InputAdornment position="end">
          <MdsIcon fontSize="small" icon={MdsCalendarToday} />
        </InputAdornment>
      }
      fullWidth
      inputProps={{
        readOnly: true,
        "data-cy": "ConditionInput-valueInput-date",
      }}
      onChange={() => {}}
      onClick={() => setOpen?.((prev) => !prev)}
      readOnly
      sx={{ px: 1 }}
      value={label ?? ""}
    />
  );
}
